import React, { useMemo } from 'react';
import { Breadcrumbs, Link, Typography } from '@mui/material';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { resolveRouteBreadcrumbs } from '../utils/routeBreadcrumbs';

interface HeaderBreadcrumbsProps {
  hidden?: boolean;
}

const HeaderBreadcrumbs: React.FC<HeaderBreadcrumbsProps> = ({ hidden = false }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const segments = useMemo(
    () => resolveRouteBreadcrumbs(location.pathname || '/', location.search || ''),
    [location.pathname, location.search]
  );

  if (hidden || segments.length === 0) {
    return null;
  }

  return (
    <Breadcrumbs
      aria-label="page breadcrumb"
      separator={<NavigateNextIcon fontSize="small" />}
      sx={{
        py: 0.25,
        overflow: 'hidden',
        '& .MuiBreadcrumbs-ol': {
          flexWrap: 'nowrap',
          overflow: 'hidden',
        },
        '& .MuiBreadcrumbs-li': {
          display: 'flex',
          alignItems: 'center',
          minWidth: 0,
          maxWidth: { xs: '45vw', sm: 'none' },
        },
        '& .MuiBreadcrumbs-separator': { mx: 0.25, flexShrink: 0 },
      }}
    >
      {segments.map((segment, index) => {
        const isLast = index === segments.length - 1;

        if (!isLast && segment.path) {
          return (
            <Link
              key={`${segment.label}-${index}`}
              component="button"
              type="button"
              underline="hover"
              color="inherit"
              onClick={() => navigate(segment.path!)}
            sx={{
              border: 0,
              background: 'none',
              cursor: 'pointer',
              px: 0,
              py: 0,
              boxSizing: 'border-box',
              display: 'inline-flex',
              alignItems: 'center',
              minHeight: 24,
              lineHeight: '24px',
              font: 'inherit',
              fontSize: '0.8125rem',
              fontWeight: 500,
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            >
              {segment.label}
            </Link>
          );
        }

        return (
          <Typography
            key={`${segment.label}-${index}`}
            variant="body2"
            color={isLast ? 'text.primary' : 'text.secondary'}
            sx={{
              fontSize: '0.8125rem',
              fontWeight: isLast ? 600 : 500,
              display: 'inline-flex',
              alignItems: 'center',
              minHeight: 24,
              lineHeight: '24px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {segment.label}
          </Typography>
        );
      })}
    </Breadcrumbs>
  );
};

export default HeaderBreadcrumbs;
